import { AlertType, Button } from '@toggle/design-system';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';

import {
  AssetDetailsWithStatus,
  Expectation,
  PrimaryStatus,
  ScenarioEnum,
  SecondaryStatus,
} from '@/api/asset/asset-schema';
import { EarningsChart } from '@/components/earnings-chart/EarningsChart';
import { ExpectationsSwitcher } from '@/components/expectations-switcher/ExpectationsSwitcher';
import { PastEarningsDrawer } from '@/components/past-earnings-drawer/PastEarningsDrawer';
import { TwitterLink } from '@/components/twitter-link/TwitterLink';
import { createLogGA } from '@/utils/analytics/logGA';
import {
  checkEarningEstimate,
  EXPECTATION_KEY,
} from '@/utils/asset/assetUtils';

import { ExpectationReported } from '../expectation-reported/ExpectationReported';
import { OpenDrawerButton } from './components/open-drawer-button/OpenDrawerButton';
import * as S from './EarningsWidget.styles';

export interface EarningsWidgetProps {
  assetDetails: AssetDetailsWithStatus;
  initExpectation: Expectation;
  activeScenario: ScenarioEnum;
  setActiveScenario: (scenario: ScenarioEnum) => void;
  chartAlt: string;
  url: string;
  twitterMessage: {
    preReported: string;
    postReported: string;
  };
}

export const EarningsWidget = ({
  assetDetails,
  activeScenario,
  setActiveScenario,
  initExpectation,
  chartAlt,
  twitterMessage,
  url,
}: EarningsWidgetProps) => {
  const { t } = useTranslation('earnings');
  const { query, pathname, replace } = useRouter();
  const assetName = query.asset as string;
  const [expectation, setExpectation] = useState(initExpectation);

  useEffect(() => {
    if (initExpectation) {
      setExpectation(initExpectation);
    }
  }, [initExpectation]);

  const onExpectationChange = (expectation: Expectation) => {
    setExpectation(expectation);
    if (!assetDetails.reported) {
      const newQuery = { ...query, [EXPECTATION_KEY]: expectation };
      const newUrl = `${pathname}?${new URLSearchParams(newQuery)}`;
      replace(newUrl, undefined, { scroll: false });
    }

    createLogGA('EPS_expect_tab')('click_eps_tab', {
      assetName,
      epsTab: expectation === Expectation.Beat ? 'beats' : 'misses',
      releaseDate: assetDetails.reporting_date,
    });
  };

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    if (assetDetails.reported) {
      setActiveScenario(ScenarioEnum.Actual);
    } else if (expectation === Expectation.Beat) {
      setActiveScenario(ScenarioEnum.Beat);
    } else {
      setActiveScenario(ScenarioEnum.Miss);
    }
  }, [assetDetails, expectation]);

  const { primary, secondary } = assetDetails.status;
  const hasValidEstimate = checkEarningEstimate(assetDetails.eps);
  const selectedScenario = assetDetails.scenarios[activeScenario];
  const historyMetaCount = selectedScenario?.history?.meta.count;
  const hasNoHistory = [PrimaryStatus.None, PrimaryStatus.OnlyEstimate];

  const showSearchAssetButton =
    primary !== PrimaryStatus.Both && secondary === SecondaryStatus.Reported;

  const isEmptyState =
    hasNoHistory.includes(primary) ||
    (primary === PrimaryStatus.OnlyHistory &&
      secondary === SecondaryStatus.Reported);

  const openTableDrawer = () => {
    setIsDrawerOpen(true);

    createLogGA('dig_in')('click_dig_in', {
      assetName,
      tab: expectation,
      releaseStatus: assetDetails.reported ? 'Released' : 'Upcoming',
    });
  };

  const onDrawerClose = () => {
    setIsDrawerOpen(false);
    createLogGA('Close')('click_close_x', { location: 'drawer' });
  };

  const onSearchDifferentAsset = () => {
    document.dispatchEvent(new CustomEvent('openSearch'));
    createLogGA('Search')('click_search_different', { assetName });
  };

  const getQuarterDisplay = () => `
    ${assetDetails.fiscal_period}’${assetDetails.fiscal_year
    .toString()
    .slice(-2)}`;

  return (
    <S.EarningsWidgetRoot data-testid="earnings-widget">
      <PastEarningsDrawer
        assetDetails={assetDetails}
        isOpen={isDrawerOpen}
        closeDrawer={onDrawerClose}
        ticker={assetDetails.ticker}
        expectation={expectation}
        scenario={selectedScenario}
      />

      {assetDetails.status.secondary === SecondaryStatus.Analyzing && (
        <S.AnalyzeAlert
          type={AlertType.Info}
          variant="inline"
          title={t('earnings:earningsBeingAnalyzed', {
            quarter: getQuarterDisplay(),
          })}
          data-testid="expectation-being-analyzed"
        />
      )}

      <S.WidgetGroup>
        <S.WidgetBadge label={t('earnings:widgetTitle')} />

        {assetDetails.reported && hasValidEstimate && (
          <ExpectationReported
            earning={assetDetails.eps}
            currency={assetDetails.currency}
          />
        )}

        {!assetDetails.reported && (
          <ExpectationsSwitcher
            expectation={expectation}
            onChange={onExpectationChange}
          />
        )}

        <EarningsChart
          assetDetails={assetDetails}
          assetName={assetName}
          expectation={expectation}
          historyMetaCount={historyMetaCount}
          scenario={selectedScenario}
          chartAlt={chartAlt}
          isEmptyState={isEmptyState}
        />
        {!isEmptyState && (
          <OpenDrawerButton
            assetDetails={assetDetails}
            expectation={expectation}
            historyMetaCount={historyMetaCount}
            onClick={openTableDrawer}
          />
        )}
      </S.WidgetGroup>

      {!isEmptyState && (
        <TwitterLink
          assetName={assetName}
          reported={assetDetails.reported}
          twitterMessage={twitterMessage}
          url={url}
        />
      )}

      {showSearchAssetButton && (
        <Button
          label={t('earnings:empty.button')}
          data-testid="search-asset-button"
          onClick={onSearchDifferentAsset}
          size="small"
        />
      )}
    </S.EarningsWidgetRoot>
  );
};
